import { useState } from 'react';
import { Formik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import './BodySystemsTab.scss';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import { useDispatch, useSelector } from 'react-redux';
import UploadPhoto from '../../UploadPhoto';
import { deleteFile } from '../../../api/fileStorage';
import { RootState } from '../../../store/store';
import {
  updateBodySystem,
  createBodySystem,
  getBodySystemsList
} from '../../../api/body-systems';
import { setBodySystemsList, setBsCount } from '../../../store/appSlice';
import { applicableModalities } from '../../../utils/constants';

type TypeName = {
  isOpen: boolean;
  onClose: () => void;
};

interface FormValues {
  title: string;
  description: string;
  photo: string;
  applicable_modalities: string[];
}

const UpdateBodySystemModal: React.FC<TypeName> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<boolean>(false);
  const [wasModalChanged, setWasModalChanged] = useState<boolean>(false);

  const { bodySystemToEdit, bsCount } = useSelector(
    (state: RootState) => state.app
  );

  const initialValues: FormValues = bodySystemToEdit
    ? {
      title: bodySystemToEdit.title,
      description: bodySystemToEdit.description,
      photo: bodySystemToEdit.icon!,
      applicable_modalities: bodySystemToEdit.applicable_modalities ?? []
    }
    : {
      title: '',
      description: '',
      photo: '',
      applicable_modalities: []
    };

  const onPhotoChange = (result: string) => {
    setWasModalChanged(true);
  };

  const onModalClose = (photo: string) => {
    if (wasModalChanged) {
      deleteFile({ file_keys: [photo] });
    }
    onClose();
  };

  const handleModalityUpdate = (
    key: string,
    isChecked: boolean,
    currentModalities: string[],
    setFieldValue: (field: string, value: string[]) => void
  ) => {
    if (isChecked) {
      setFieldValue(
        'applicable_modalities',
        currentModalities.includes(key)
          ? currentModalities
          : [...currentModalities, key]
      );
      return;
    }

    setFieldValue(
      'applicable_modalities',
      currentModalities.filter((item) => item !== key)
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, { resetForm }) => {
        if (isFormError) setIsFormError(false);
        setIsLoading(true);
        try {
          const bsData = {
            title: values.title,
            description: values.description,
            icon: values.photo,
            applicable_modalities: values.applicable_modalities
          };

          if (bodySystemToEdit) {
            await updateBodySystem({
              ...bsData,
              id: bodySystemToEdit.id!
            });

            // Always refetch to ensure UI reflects server-side changes
            // (especially updated `icon_url` after photo upload).
            const response = await getBodySystemsList({ prompt: true });
            dispatch(setBodySystemsList(response.body_systems));
          } else {
            await createBodySystem(bsData);
            const response = await getBodySystemsList({ prompt: true });
            dispatch(setBodySystemsList(response.body_systems));
            dispatch(setBsCount(bsCount ? bsCount + 1 : 0));
          }

          onClose();
          resetForm();
        } catch (error) {
          console.error('Error creating doctor:', error);
          setIsFormError(true);
        } finally {
          setIsLoading(false);
        }
      }}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ handleSubmit, values, submitCount, errors, setFieldValue }) => (
        <Modal
          isOpen={isOpen}
          onClose={() => onModalClose(values.photo!)}
          title={'Add Body System'}
          width={'520px'}
        >
          <form
            onSubmit={handleSubmit}
            noValidate
            className='bs-modal-container'
          >
            <div className='main-info-container'>
              <div className='upload-photo-wrapper'>
                <UploadPhoto
                  onPhotoChange={onPhotoChange}
                  putToStaticFolder={true}
                  defaultPhoto={bodySystemToEdit?.icon_url}
                />
              </div>
              <div className='inputs-wrapper'>
                <TextInput
                  title='Name *'
                  name='name'
                  placeholder='Enter name'
                  type='text'
                  isError={submitCount > 0 && !!errors.title}
                  errorMessage={submitCount > 0 ? errors.title : undefined}
                  onChange={(e) => setFieldValue('title', e.target.value)}
                  value={values.title}
                />
                <TextArea
                  title='Text *'
                  name='description'
                  placeholder='Enter description'
                  isError={submitCount > 0 && !!errors.description}
                  errorMessage={
                    submitCount > 0 ? errors.description : undefined
                  }
                  onChange={(e) => setFieldValue('description', e.target.value)}
                  value={values.description}
                  rows={10}
                />
              </div>
              <div className='modalities-wrapper'>
                <span className='text-input-title'>Applicable Modalities</span>
                <div className='modalities-list'>
                  {applicableModalities.map((modality) => (
                    <label key={modality.key} className='modality-item'>
                      <input
                        type='checkbox'
                        checked={values.applicable_modalities.includes(modality.key)}
                        onChange={(event) =>
                          handleModalityUpdate(
                            modality.key,
                            event.target.checked,
                            values.applicable_modalities,
                            setFieldValue
                          )
                        }
                        className='modality-checkbox'
                      />
                      <span className='modality-label'>{modality.value}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className='btn-wrapper'>
              <Button
                text={bodySystemToEdit ? 'Edit body system' : 'Add body system'}
                type='submit'
                isLoading={isLoading}
                isDisabled={
                  !values.title || !values.description || !values.photo
                }
              />
              {isFormError && (
                <div className='error-message'>
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        </Modal>
      )}
    </Formik>
  );
};

export default UpdateBodySystemModal;
